import React from "react";
import { TH_ACTUAL_TEXT } from "../../reportTableConfig";

export const WATERFALL_MONTH_KEYS = [
  "jan", "feb", "mar", "apr", "may", "jun",
  "jul", "aug", "sep", "oct", "nov", "dec",
];

export const METRIC_TOTAL_TOGGLE_KEY = "_total";

export function metricTotalColKey(groupKey) {
  return `${groupKey}_total`;
}

/** Format percentage for table cells; blank only when missing, show 0.0% for zero. */
export function fmtPctDisplay(v) {
  if (v == null || v === "") return "";
  const n = Number(v);
  if (!Number.isFinite(n)) return "";
  return `${n.toFixed(1)}%`;
}

/** Text wraps; numbers/currency stay on one line. */
export function cellWhiteSpaceStyle(isNumeric) {
  if (isNumeric) {
    return { whiteSpace: "nowrap", overflow: "hidden" };
  }
  return {
    whiteSpace: "normal",
    wordBreak: "break-word",
    overflowWrap: "anywhere",
    overflow: "hidden",
    lineHeight: 1.35,
  };
}

/**
 * Column size for sticky/aligned tables (table-layout: fixed).
 * Widths must stay consistent so sticky `left` offsets match real columns.
 */
export function dynamicColSize(w) {
  return { width: w, minWidth: w, maxWidth: w };
}

/** Full-year total for a measure: API total when present, else sum of month values (non-% only). */
export function metricTotalValue(measureMap, isPct, monthKeys = WATERFALL_MONTH_KEYS) {
  const m = measureMap || {};
  const isPeriodSubset =
    monthKeys.length < WATERFALL_MONTH_KEYS.length
    || !WATERFALL_MONTH_KEYS.every((k) => monthKeys.includes(k));
  if (!isPeriodSubset && m.total != null && m.total !== "") return m.total;
  if (isPct) return null;
  let sum = 0;
  let any = false;
  for (const mk of monthKeys) {
    const v = m[mk];
    if (v != null && v !== "") {
      sum += Number(v);
      any = true;
    }
  }
  return any ? sum : null;
}

/** Cycle column sort: none → asc → desc → none (multi-level when different columns). */
export function toggleSortKey(prev, colKey) {
  const existing = prev.find((s) => s.key === colKey);
  if (!existing) return [...prev, { key: colKey, dir: "asc" }];
  if (existing.dir === "asc") return prev.map((s) => (s.key === colKey ? { ...s, dir: "desc" } : s));
  return prev.filter((s) => s.key !== colKey);
}

export function compareBySortKeys(a, b, sortKeys, getValue) {
  for (const { key, dir } of sortKeys) {
    const av = getValue(a, key);
    const bv = getValue(b, key);
    let cmp;
    if (typeof av === "string" || typeof bv === "string") {
      cmp = String(av ?? "").localeCompare(String(bv ?? ""), undefined, {
        numeric: true,
        sensitivity: "base",
      });
    } else {
      cmp = (Number(av) || 0) - (Number(bv) || 0);
    }
    if (cmp !== 0) return dir === "asc" ? cmp : -cmp;
  }
  return 0;
}

export function createSortThClick(handleSort) {
  return (colKey) => (e) => {
    const el = e.target instanceof Element ? e.target : e.target?.parentElement;
    if (el?.closest?.("[data-filter-btn]") || el?.closest?.("[data-op-btn]")) return;
    handleSort(colKey);
  };
}

const SORT_ICON_STYLE = {
  flexShrink: 0,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  lineHeight: 1,
};

function SortChevrons({ active, dir, tone = "default" }) {
  const isActual = tone === "actual";
  const activeColor = isActual ? TH_ACTUAL_TEXT : "#FFFFFF";
  const idleColor = isActual ? "rgba(0,0,0,0.45)" : "rgba(213,218,224,0.72)";
  if (active) {
    return (
      <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true" style={SORT_ICON_STYLE}>
        {dir === "asc" ? (
          <path d="M5 2L8.5 7.5H1.5Z" fill={activeColor} />
        ) : (
          <path d="M5 8L1.5 2.5H8.5Z" fill={activeColor} />
        )}
      </svg>
    );
  }
  return (
    <svg width="10" height="12" viewBox="0 0 10 12" aria-hidden="true" style={SORT_ICON_STYLE}>
      <path d="M5 1.5L8 5.5H2Z" fill={idleColor} />
      <path d="M5 10.5L2 6.5H8Z" fill={idleColor} />
    </svg>
  );
}

export function WaterfallSortIcon({ sortKeys, colKey, getLabel, tone = "default" }) {
  const sortTitle = `Sort by ${getLabel(colKey)}`;
  const idx = sortKeys.findIndex((s) => s.key === colKey);
  const active = idx !== -1;
  const dir = active ? sortKeys[idx].dir : null;
  const isActual = tone === "actual";
  return (
    <span
      title={sortTitle}
      data-sort-icon
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 1,
        flexShrink: 0,
        lineHeight: 1,
        marginLeft: 2,
      }}
    >
      <SortChevrons active={active} dir={dir} tone={tone} />
      {active && sortKeys.length > 1 && (
        <sup style={{ fontSize: 8, lineHeight: 1, color: isActual ? TH_ACTUAL_TEXT : "#FFFFFF" }}>{idx + 1}</sup>
      )}
    </span>
  );
}

/** Label + sort icon grouped in header cells. */
export function WaterfallThLabel({
  label,
  colKey,
  sortKeys,
  getLabel,
  highlight,
  align = "left",
  textColor,
  tone = "default",
  wrap = false,
}) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: wrap ? "flex-end" : "center",
        gap: 4,
        flex: align === "right" ? undefined : "1 1 auto",
        minWidth: wrap ? 0 : undefined,
      }}
    >
      <span
        style={{
          color: highlight ? "#60A5FA" : textColor,
          whiteSpace: wrap ? "normal" : "nowrap",
          wordBreak: wrap ? "break-word" : undefined,
          lineHeight: wrap ? 1.25 : undefined,
        }}
      >
        {label}
      </span>
      <WaterfallSortIcon sortKeys={sortKeys} colKey={colKey} getLabel={getLabel} tone={tone} />
    </span>
  );
}
